import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { User, Users, Package, Menu, X, GraduationCap, Home, Receipt, BookOpen, ChevronLeft, Bell, ClipboardList, LogOut, Crown, FileEdit, Upload, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase, getSupabaseUrl } from '../../supabase/client';

const menuItems = [
  { path: '/teacher/basic-info', label: '基本信息', icon: User },
  { path: '/teacher/students', label: '学员管理', icon: Users },
  { path: '/teacher/assignment-publish', label: '发布作业', icon: ClipboardList },
  { path: '/teacher/assignments', label: '阅卷与评讲', icon: BookOpen },
  { path: '/teacher/paper-editor', label: '试卷编辑', icon: FileEdit },
  { path: '/teacher/paper-upload', label: '试卷上传', icon: Upload },
  { path: '/teacher/papers', label: '试卷列表', icon: FileText },
  { path: '/teacher/orders', label: '套餐与订单', icon: Receipt },
  { path: '/teacher/gift-points', label: '我的U币', icon: Crown },
];

export default function TeacherLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [teacherInfo, setTeacherInfo] = useState<any>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [accessToken, setAccessToken] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    loadTeacherInfo();
  }, []);

  const loadTeacherInfo = async () => {
    // getUser() 和 getSession() 可能因 Supabase auth lock 竞争导致 AbortError
    let user: any = null;
    try {
      const { data } = await supabase.auth.getUser();
      user = data.user;
    } catch (lockError: any) {
      if (lockError?.name === 'AbortError' || lockError?.message?.includes('Lock broken')) {
        const { data } = await supabase.auth.getUser();
        user = data.user;
      } else {
        throw lockError;
      }
    }
    if (!user) {
      navigate('/login');
      return;
    }

    let session: any = null;
    try {
      const { data } = await supabase.auth.getSession();
      session = data.session;
    } catch (lockError: any) {
      if (lockError?.name === 'AbortError' || lockError?.message?.includes('Lock broken')) {
        const { data } = await supabase.auth.getSession();
        session = data.session;
      } else {
        throw lockError;
      }
    }
    const token = session?.access_token || '';
    setAccessToken(token);

    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (!profileData || profileData.role !== 'teacher') {
      navigate('/');
      return;
    }

    setTeacherInfo(profileData);
    loadUnreadCount(user.id, token);
  };

  const loadUnreadCount = async (userId: string, token: string) => {
    try {
      if (!token) return;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'messages_get_unread_count', userId }),
      });
      const result = await response.json();
      if (result.success) {
        setUnreadCount(result.count);
      }
    } catch (error) {
      console.error('加载未读消息数失败:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className={`bg-white border-r border-gray-200 transition-all duration-300 flex flex-col ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-gray-800">教师中心</span>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-600">
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
        <nav className="p-3 space-y-1 flex-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                isActive(item.path)
                  ? 'bg-purple-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-purple-50 hover:text-purple-600'
              }`}
            >
              <item.icon size={18} className={isActive(item.path) ? 'text-white' : 'text-gray-500'} />
              {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* 退出登录按钮 */}
        <div className="p-3 border-t border-gray-200 mt-auto">
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all w-full ${
              sidebarOpen
                ? 'text-red-600 hover:bg-red-50'
                : 'text-red-600 hover:bg-red-50 justify-center'
            }`}
          >
            <LogOut size={18} className="text-red-500" />
            {sidebarOpen && <span className="text-sm font-medium">退出登录</span>}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors shadow-md"
            >
              <Home size={18} />
              <span className="text-sm font-medium">首页</span>
            </Link>
            <Link
              to="/exam-categories"
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors shadow-md"
            >
              <BookOpen size={18} />
              <span className="text-sm font-medium">刷题平台</span>
            </Link>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors shadow-md"
            >
              <ChevronLeft size={18} />
              <span className="text-sm font-medium">返回</span>
            </button>
            {/* 站内信 - 放在返回按钮右边 */}
            <Link
              to="/teacher/messages"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-500 text-white rounded-xl shadow-lg shadow-amber-200/50 hover:shadow-amber-300/60 hover:scale-105 transition-all duration-300 group"
            >
              {/* 信封图标容器 */}
              <div className="relative">
                <svg
                  className="w-6 h-6 text-white drop-shadow-sm"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {/* 信封主体 */}
                  <rect x="2" y="5" width="20" height="14" rx="2" className="fill-amber-100/20" />
                  {/* 信封翻盖 - V形 */}
                  <path d="M2 7l10 7 10-7" className="stroke-white" />
                  {/* 信封底部装饰线 */}
                  <path d="M2 17l6-4M22 17l-6-4" className="stroke-white/60" />
                </svg>
                {/* 未读数红点 */}
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-md border-2 border-amber-400 animate-pulse">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="text-sm font-bold text-white drop-shadow-sm">站内信</span>
            </Link>
          </div>
          {/* 欢迎信息 - 居中 */}
          <div className="flex-1 flex items-center justify-center">
            <span className="text-sm text-gray-600">欢迎你，{teacherInfo?.teacher_nickname || teacherInfo?.username || '老师'}</span>
          </div>
          {/* 占位元素保持布局平衡 */}
          <div className="w-[200px]"></div>
        </header>
        <main className="flex-1 p-6 overflow-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}